import { useState, useEffect, useRef } from 'react'
import { Box, Stack, TextField, Typography, Button } from '@mui/material'
import BasicModal from '@publish/components/Modal/BasicModal'
import { useIsMobile } from '@/hooks/Responsive'
import { useAuth } from '@/components/AuthContext'
import { insertMyQuestionData } from '@/api/mypage'

export default function InquiryDialog({
  open,
  onClose,
  defaultValue,
  data,
}) {
  const { isMobile } = useIsMobile()
  const { user } = useAuth()

  const [inquiryTitle, setInquiryTitle] = useState('')
  const [inquiryContent, setInquiryContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const initializedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      initializedRef.current = false
      return
    }

    if (initializedRef.current) return
    initializedRef.current = true

    setInquiryTitle(defaultValue?.title || '')
    setInquiryContent(defaultValue?.content || '')
  }, [open, defaultValue])

  const handleSubmit = async () => {
    if (!user?.accountId) {
      alert('로그인 정보가 없습니다.')
      return
    }
    
    // console.log(data)

    const eduLectureId = data?.id
    if (!eduLectureId) {
      alert('강좌 정보가 없습니다.')
      return
    }

    if (!inquiryTitle.trim()) {
      alert('제목을 입력해 주세요.')
      return
    }
    if (!inquiryContent.trim()) {
      alert('내용을 입력해 주세요.')
      return
    }

    setIsSubmitting(true)

    const formData = new FormData()

    const question = {
      eduLectureId,
      bbsType: 'lecture',
      bulletinTitle: inquiryTitle.trim(),
      bulletinSubstatnce: inquiryContent.trim(),
      accountId: user.accountId,
    }

    formData.append(
      'question',
      new Blob([JSON.stringify(question)], { type: 'application/json' })
    )

    try {
      const response = await insertMyQuestionData(formData)
      if (response?.data !== '0') {
        alert('문의가 등록되었습니다.')
        onClose?.()
      } else {
        alert('문의 등록에 실패했습니다.')
      }
    } catch (error) {
      console.error('Failed to submit inquiry:', error)
      alert('문의 등록 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title="문의하기"
      variant="bottomsheet"
      actions={
        <>
          {!isMobile && (
            <Button
              variant="outlined"
              size="medium"
              color="grey"
              onClick={onClose}
              disabled={isSubmitting}
            >
              취소
            </Button>
          )}

          <Button
            variant="contained"
            size={isMobile ? 'large' : 'medium'}
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? '등록중' : '등록'}
          </Button>
        </>
      }
    >
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Typography fontWeight={500}>작성자</Typography>

          <Box
            sx={{
              width: '1px',
              height: '16px',
              bgcolor: 'divider',
              mx: 1,
            }}
          />

          <Typography>{user?.name || '-'}</Typography>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Typography fontWeight={500} sx={{ flexShrink: 0 }}>
            강좌명
          </Typography>

          <Box
            sx={{
              width: '1px',
              height: '16px',
              bgcolor: 'divider',
              mx: 1,
              mt: '5px !important',
            }}
          />

          <Typography>{data?.title || '-'}</Typography>
        </Stack>

        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography component="label" htmlFor="inquiryTitle" fontWeight={500}>
            제목 <Typography component="span" color="error.main">*</Typography>
          </Typography>

          <TextField
            id="inquiryTitle"
            placeholder="제목을 입력해 주세요"
            fullWidth
            value={inquiryTitle}
            onChange={(e) => setInquiryTitle(e.target.value)}
            disabled={isSubmitting}
          />
        </Stack>

        <Stack spacing={1} sx={{ position: 'relative' }}>
          <Typography component="label" htmlFor="inquiryContent" fontWeight={500}>
            내용 <Typography component="span" color="error.main">*</Typography>
          </Typography>

          <TextField
            id="inquiryContent"
            placeholder="내용을 입력해 주세요"
            multiline
            minRows={3}
            fullWidth
            value={inquiryContent}
            onChange={(e) => setInquiryContent(e.target.value)}
            inputProps={{ maxLength: 1500 }}
            disabled={isSubmitting}
            sx={{
              '& .MuiInputBase-inputMultiline': {
                pb: 4,
              },
            }}
          />

          <Typography
            variant="body3"
            sx={{
              position: 'absolute',
              right: '16px',
              bottom: '16px',
              color: 'text.secondary',
            }}
          >
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                Typography: 'body3',
              }}
            >
              {inquiryContent.length}
            </Box>
            자 / 1,500자
          </Typography>
        </Stack>

        <Stack spacing={0.5} sx={{ mt: 2 }}>
          <Typography color="text.secondary">
            * 문의에 대한 답변은{' '}
            <Box component="span" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              마이페이지 &gt; 나의수강정보 &gt; 나의문의
            </Box>{' '}
            에서 확인해 주세요.
          </Typography>

          <Typography color="text.secondary">
            * 강의 내용과 관련 없는 문의에는 답변을 드리지 않을 수 있는 점 참고해 주세요.
          </Typography>
        </Stack>
      </Box>
    </BasicModal>
  )
}
